import { createContext, useContext } from 'react';

import {
  type ExperienceMode,
  type GlossaryEntry,
  type PersonaLens,
  PORTAL_GLOSSARY,
} from './glossary';

export interface PortalExperienceContextValue {
  readonly mode: ExperienceMode;
  readonly isPlainMode: boolean;
  readonly isExpertMode: boolean;
  readonly personaLens: PersonaLens;
  readonly setMode: (next: ExperienceMode) => void;
  readonly toggleMode: () => void;
  readonly setPersonaLens: (lens: PersonaLens) => void;
  readonly getTerm: (key: string) => GlossaryEntry | undefined;
  readonly formatLabel: (plainText: string, expertText: string) => string;
}

export function inferDefaultLensFromEmail(email?: string): PersonaLens {
  const normalized = (email ?? '').toLowerCase();
  if (!normalized) {
    return 'operations';
  }
  if (normalized === 'syllas@giyus.com.br') {
    return 'admin';
  }
  if (
    normalized.includes('chong') ||
    normalized.includes('nelson') ||
    normalized.includes('marco')
  ) {
    return 'clinical';
  }
  if (normalized.includes('compras') || normalized.includes('fin')) {
    return 'financial';
  }
  if (!normalized.endsWith('@giyus.com.br')) {
    return 'external';
  }
  return 'operations';
}

export const PortalExperienceContext = createContext<PortalExperienceContextValue | null>(null);

const FALLBACK_CONTEXT: PortalExperienceContextValue = {
  mode: 'plain',
  isPlainMode: true,
  isExpertMode: false,
  personaLens: 'operations',
  setMode: () => undefined,
  toggleMode: () => undefined,
  setPersonaLens: () => undefined,
  getTerm: (key: string) => PORTAL_GLOSSARY[key],
  formatLabel: (plainText: string) => plainText,
};

export function usePortalExperience(): PortalExperienceContextValue {
  const ctx = useContext(PortalExperienceContext);
  return ctx ?? FALLBACK_CONTEXT;
}
